import React from "react";
import { createPortal } from "react-dom";
import SimManager from "../../managers/simManager";
import ControllableObject from "../../interactableObjects/controllableObject";
import CustomObject from "../../interactableObjects/customObject";
import { CloseIcon } from "@learningblocks/storybook-components/lib/Icons";
import parse from "html-react-parser";

import "./moveMenuStyles.scss";

interface MoveableItem {
  getId(): string;
  removeMoveMenuButton?(): void;
  props: { id: string };
}

interface modalProps {
  item: MoveableItem;
  hamburgerButtonRef?: React.RefObject<HTMLElement>;
}

interface modalState {
  options: string[];
  showModal: boolean;
}

class MoveMenu extends React.Component<modalProps, modalState> {
  private parent: MoveableItem;
  private hamburgerButtonRef?: React.RefObject<HTMLElement>;

  constructor(props: modalProps) {
    super(props);

    this.parent = props.item;
    this.hamburgerButtonRef = props.hamburgerButtonRef;
    this.state = {
      options: [],
      showModal: false,
    };
  }

  setMoveMenuOptions(options: string[]) {
    this.setState({ options: options });
  }

  setHamburgerButtonRef(ref: React.RefObject<HTMLElement>) {
    this.hamburgerButtonRef = ref;
  }

  openMoveMenu = () => {
    this.setState({ showModal: true });
  };

  closeModal = () => {
    this.setState({ showModal: false });

    // Focus back to the hamburger menu button with a slight delay
    setTimeout(() => {
      if (this.hamburgerButtonRef?.current) {
        this.hamburgerButtonRef.current.focus();
      }
    }, 0);
  };

  onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      this.closeModal();
    }
  };

  moveMenuClicked(name: string) {
    const option: string[] = this.state.options.filter((item) => item == name);
    if (option.length > 0) {
      SimManager.getInstance().moveMenuFunction(
        option[0],
        this.parent.props.id
      );
      this.closeModal();
      this.parent.removeMoveMenuButton?.();

      // Focus back to the hamburger menu button with a slight delay
      setTimeout(() => {
        if (this.hamburgerButtonRef?.current) {
          this.hamburgerButtonRef.current.focus();
        }
      }, 0);
    }
  }

  displayOptions = () => {
    return (
      <div className="moveMenuBodyWrapper">
        {this.state.options.map((option, index) => (
          <div className="moveMenuBodyRow" key={index}>
            <div
              className="moveMenuOption"
              role="button"
              tabIndex={0}
              aria-label={`Move to ${SimManager.getInstance().getDisplayLabel(option)}`}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  this.moveMenuClicked(option);
                }
              }}
              onClick={() => this.moveMenuClicked(option)}
            >
              {parse(SimManager.getInstance().getDisplayLabel(option))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  render(): React.ReactNode {
    if (!this.state.showModal) {
      return null;
    }

    // Get the dragdrop-holder element or fallback to document.body
    const portalTarget =
      (typeof document !== "undefined" &&
        document.getElementById("dragdrop-holder")) ||
      (typeof document !== "undefined" && document.body) ||
      null;

    if (!portalTarget) {
      return null;
    }

    return createPortal(
      <div
        className="moveMenuBackground"
        tabIndex={0}
        ref={(el) => el?.focus()}
      >
        <div className="moveMenuContainer">
          <div className="moveMenuHeadingWrapper">
            <div className="moveMenuHeadingText">
              Select where to move the component
            </div>
            <div className="closeButton">
              <span
                className="closeIcon"
                role="button"
                tabIndex={0}
                aria-label="Close move menu"
                onKeyDown={this.onKeyDown}
                onClick={this.closeModal}
              >
                <img src={CloseIcon} alt="Close" />
              </span>
            </div>
          </div>
          <this.displayOptions />
        </div>
      </div>,
      portalTarget
    ) as React.ReactNode;
  }
}

export default MoveMenu;
